"use client";

import {
  MergedContent,
  fetchInvestPortfolioAssetsFromSummary,
  genPerformance,
  returnZeroIfInvalidNumber,
} from "../utils/portfolio";
import { keyBy, maxBy } from "lodash-es";
import { useCallback, useMemo } from "react";

import { QUERY_KEYS } from "@config/constant";
import { ALL_PORTFOLIO_OPTION_ID } from "../components/PortfolioContainer/constants";
import { Stock } from "../features/api/openData";
import bigDecimal from "js-big-decimal";
import { dayjs } from "utils";
import { rootStore } from "@store/rootStore";
import { sleep } from "../utils/sleep";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { useParams } from "next/navigation";
import { useQueries } from "@tanstack/react-query";
import { useSnapshot } from "valtio/react";

/**
 * 将资产价值从 HKD 或 CNY 转换为 USD
 * @param curr 当前资产内容
 * @param forex 汇率数据
 * @returns 转换后的5个值（如果不需要转换则返回原值）
 */
const convertToUSD = (
  curr: MergedContent,
  forex?: Record<string, any>,
): {
  value: number;
  prevValue: number;
  cumulativeNetBuyValue: number;
  cumulativeBuyValue: number;
  cumulativeFee: number;
  todayPnl: number;
  todayIncrementBuyValueTotal: number;
  todayUnPnl: number;
  totalPnl: number;
} => {
  let value = returnZeroIfInvalidNumber(curr.value);
  let prevValue = returnZeroIfInvalidNumber(curr.prevValue);
  let cumulativeNetBuyValue = returnZeroIfInvalidNumber(
    curr.cumulative_net_buy_value,
  );
  let cumulativeBuyValue = returnZeroIfInvalidNumber(curr.cumulative_buy_value);
  let cumulativeFee = returnZeroIfInvalidNumber(curr.cumulative_fee);
  let todayPnl = returnZeroIfInvalidNumber(curr.today_pnl);
  let todayIncrementBuyValueTotal = returnZeroIfInvalidNumber(
    curr.today_increment_buy_value_total,
  );
  let todayUnPnl = returnZeroIfInvalidNumber(curr.today_un_pnl);
  let totalPnl = returnZeroIfInvalidNumber(curr.total_pnl);
  // 汇率转换：如果资产货币是 HKD 或 CNY，需要转换为 USD
  if (forex && curr.asset?.currency) {
    let exchangeRate: number | null = null;

    if (
      curr.asset.currency === "HKD" &&
      forex["USD_HKD"]?.market_data?.current_price
    ) {
      exchangeRate = returnZeroIfInvalidNumber(
        Number(forex["USD_HKD"].market_data.current_price),
      );
    } else if (
      curr.asset.currency === "CNY" &&
      forex["USD_CNY"]?.market_data?.current_price
    ) {
      exchangeRate = returnZeroIfInvalidNumber(
        Number(forex["USD_CNY"].market_data.current_price),
      );
    }

    // 如果获取到有效汇率，将5个值转换为USD（除以汇率）
    if (exchangeRate && exchangeRate > 0) {
      value = Number(
        new bigDecimal(value).divide(new bigDecimal(exchangeRate)).getValue(),
      );
      prevValue = Number(
        new bigDecimal(prevValue)
          .divide(new bigDecimal(exchangeRate))
          .getValue(),
      );
      cumulativeNetBuyValue = Number(
        new bigDecimal(cumulativeNetBuyValue)
          .divide(new bigDecimal(exchangeRate))
          .getValue(),
      );
      cumulativeBuyValue = Number(
        new bigDecimal(cumulativeBuyValue)
          .divide(new bigDecimal(exchangeRate))
          .getValue(),
      );
      cumulativeFee = Number(
        new bigDecimal(cumulativeFee)
          .divide(new bigDecimal(exchangeRate))
          .getValue(),
      );
      todayPnl = Number(
        new bigDecimal(todayPnl)
          .divide(new bigDecimal(exchangeRate))
          .getValue(),
      );
      todayIncrementBuyValueTotal = Number(
        new bigDecimal(todayIncrementBuyValueTotal)
          .divide(new bigDecimal(exchangeRate))
          .getValue(),
      );
      todayUnPnl = Number(
        new bigDecimal(todayUnPnl)
          .divide(new bigDecimal(exchangeRate))
          .getValue(),
      );
      totalPnl = Number(
        new bigDecimal(totalPnl)
          .divide(new bigDecimal(exchangeRate))
          .getValue(),
      );
    }
  }

  return {
    value,
    prevValue,
    cumulativeNetBuyValue,
    cumulativeBuyValue,
    cumulativeFee,
    todayPnl,
    todayUnPnl,
    todayIncrementBuyValueTotal,
    totalPnl,
  };
};

// 对所有value 和 prevValue 求和
const sumValueAndPrevValue = (
  acc: {
    todayValue: number;
    prevValue: number;
    cumulativeNetBuyValue: number;
    cumulativeBuyValue: number;
    cumulativeFee: number;
    positiveValue: number;
    forex?: Record<string, any>;
    source: (MergedContent &
      Record<
        | "value_usd"
        | "prevValue_usd"
        | "cumulativeNetBuyValue_usd"
        | "cumulativeBuyValue_usd"
        | "cumulativeFee_usd"
        | "todayPnl_usd"
        | "todayIncrementBuyValueTotal_usd"
        | "todayUnPnl_usd"
        | "totalPnl_usd",
        number
      >)[];
    totalTodayPnl: number;
    todayIncrementBuyValueTotal: number;
    todayUnPnl: number;
  },
  curr: MergedContent,
) => {
  // 获取转换后的值（如果需要汇率转换则已转换，否则保持原值）
  const {
    value,
    prevValue,
    cumulativeNetBuyValue,
    cumulativeBuyValue,
    cumulativeFee,
    todayPnl,
    todayUnPnl,
    todayIncrementBuyValueTotal,
    totalPnl,
  } = convertToUSD(curr, acc.forex);

  acc.source.push({
    ...curr,
    value_usd: value,
    prevValue_usd: prevValue,
    cumulativeNetBuyValue_usd: cumulativeNetBuyValue,
    cumulativeBuyValue_usd: cumulativeBuyValue,
    cumulativeFee_usd: cumulativeFee,
    todayPnl_usd: todayPnl,
    todayIncrementBuyValueTotal_usd: todayIncrementBuyValueTotal,
    todayUnPnl_usd: todayUnPnl,
    totalPnl_usd: totalPnl,
  });

  // 使用 bigDecimal 进行精确计算
  acc.todayValue = Number(
    new bigDecimal(acc.todayValue).add(new bigDecimal(value)).getValue(),
  );
  acc.positiveValue = Number(
    new bigDecimal(acc.positiveValue)
      .add(new bigDecimal(value < 0 ? 0 : value))
      .getValue(),
  );
  acc.prevValue = Number(
    new bigDecimal(acc.prevValue).add(new bigDecimal(prevValue)).getValue(),
  );
  acc.cumulativeNetBuyValue = Number(
    new bigDecimal(acc.cumulativeNetBuyValue)
      .add(new bigDecimal(cumulativeNetBuyValue))
      .getValue(),
  );
  acc.cumulativeBuyValue = Number(
    new bigDecimal(acc.cumulativeBuyValue)
      .add(new bigDecimal(cumulativeBuyValue))
      .getValue(),
  );
  acc.cumulativeFee = Number(
    new bigDecimal(acc.cumulativeFee)
      .add(new bigDecimal(cumulativeFee))
      .getValue(),
  );
  acc.totalTodayPnl = Number(
    new bigDecimal(acc.totalTodayPnl).add(new bigDecimal(todayPnl)).getValue(),
  );
  acc.todayUnPnl = Number(
    new bigDecimal(acc.todayUnPnl).add(new bigDecimal(todayUnPnl)).getValue(),
  );
  acc.todayIncrementBuyValueTotal = Number(
    new bigDecimal(acc.todayIncrementBuyValueTotal)
      .add(
        new bigDecimal(
          todayIncrementBuyValueTotal < 0 ? 0 : todayIncrementBuyValueTotal,
        ),
      )
      .getValue(),
  );
  return acc;
};

export const usePortfolioPage = () => {
  const { portfolioId: id } = useParams<{ portfolioId: string }>();

  // const {allFavoriteData} = useSnapshot(favoritesCenter.state);
  const { openDataApi, memberApi } = useOspApiClientProvider();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const {
    data,
    isPending,
    isSuccess,
    isError,
    isTotalError,
    refetchTotal,
    isFetching,
    isLoading,
    isRefetching,
    isStale,
    assetsRefetch,
    performanceRefetch,
  } = useQueries({
    queries: [
      {
        queryKey: [QUERY_KEYS.PORTFOLIO_ASSETS_INFINITE, id],
        enabled: isLoggedIn && !!openDataApi && !!memberApi && !!id,
        staleTime: 1 * 60 * 1000, // 1 minute
        queryFn: () =>
          fetchInvestPortfolioAssetsFromSummary(id!, memberApi!, openDataApi!),
      },
      {
        // 获取汇率
        queryKey: [QUERY_KEYS.FETCH_FOREXES, "USD_HKD", "USD_CNY"],
        enabled: isLoggedIn && !!openDataApi,
        staleTime: 30 * 60 * 1000, // 30 minutes
        queryFn: async () => {
          const res = await openDataApi?.forexes.getForexes({
            limit: 2,
            ids: ["USD_HKD", "USD_CNY"],
            "enrich.enable": true,
            "enrich.with_market.enable": true,
          });
          const forexData = res?.data?.data?.rows;
          const forexDataMap = keyBy(forexData, "id");
          return forexDataMap;
        },
      },
      {
        // 获取 performance pnl 图表
        queryKey: [QUERY_KEYS.PORTFOLIO_PERFORMANCE, id, "PNL"],
        enabled: isLoggedIn && !!memberApi,
        staleTime: 1 * 60 * 1000, // 1 minute
        queryFn: async () => {
          const res = await memberApi?.positions.getOwnerPositionSummary({
            ...(id && id !== ALL_PORTFOLIO_OPTION_ID
              ? { account_ids: [id] }
              : {}),
            with_position_performance: true,
            performance_type: "VALUE",
            performance_period: "30d",
          });
          return res?.data?.data?.position_performance;
        },
      },
    ],
    combine: (results) => {
      const [assets, forex, performance] = results;
      const performanceData = performance.data?.points?.map((item) => ({
        ...item,
        label: dayjs(item.time).format("MM/DD HH:mm"),
      }));
      return {
        data: {
          assets: assets?.data,
          forex: forex?.data,
          performance: performanceData,
        },
        isPending: results.some((result) => result.isPending),
        isError: results.some((result) => result.isError),
        // 当前总额与持仓行都由同一 assets summary 派生，不能伪装成两条独立 query。
        // 因此只暴露一个 summary 错误边界；performance 查询只提供历史图，不能代表
        // 当前总额或持仓的成功/失败。
        isTotalError: assets.isError,
        refetchTotal: assets.refetch,
        isSuccess: results.every((result) => result.isSuccess),
        isFetching: results.some((result) => result.isFetching),
        isLoading: results.some((result) => result.isLoading),
        isRefetching: results.some((result) => result.isRefetching),
        isStale: results.some((result) => result.isStale),
        assetsRefetch: assets.refetch,
        performanceRefetch: performance.refetch,
      };
    },
  });
  const refetch = useCallback(async () => {
    await sleep(2500);
    await Promise.all([assetsRefetch(), performanceRefetch()]);
  }, [assetsRefetch, performanceRefetch]);
  // 计算today value总和 & change & change precent & prevValue & stockValue & tokenValue & totalCumulativePnl & totalCumulativePnlPercent ✅
  const {
    value,
    change,
    changePercent,
    totalCumulativePnlPercent,
    totalCumulativePnl,
    positiveValue,
    stockPositiveValue,
    tokenPositiveValue,
    totalTodayPnlPercent,
    totalTodayPnl,
    todayUnPnlPercent,
    todayUnPnl,
    tokenSource, // Tokens 和 Stocks 的 value pnl等值有USD转换后的值 用来计算资产占比
    stockSource,
  } = useMemo(() => {
    const assets = data?.assets;
    if (!assets?.stocks?.length && !assets?.tokens?.length) {
      return {
        value: 0,
        change: 0,
        changePercent: 0,
        totalTodayPnl: 0,
        totalTodayPnlPercent: undefined,
        totalCumulativePnl: 0,
        totalCumulativePnlPercent: undefined,
        positiveValue: 0,
        stockPositiveValue: 0,
        tokenPositiveValue: 0,
        tokenSource: [],
        stockSource: [],
        todayUnPnlPercent: undefined,
        todayUnPnl: 0,
      };
    }
    const stock = assets?.stocks.reduce(sumValueAndPrevValue, {
      todayValue: 0,
      prevValue: 0,
      cumulativeNetBuyValue: 0,
      cumulativeBuyValue: 0,
      cumulativeFee: 0,
      positiveValue: 0,
      forex: data?.forex,
      source: [],
      totalTodayPnl: 0,
      todayIncrementBuyValueTotal: 0,
      todayUnPnl: 0,
    });
    const token = assets?.tokens.reduce(sumValueAndPrevValue, {
      todayValue: 0,
      prevValue: 0,
      cumulativeNetBuyValue: 0,
      cumulativeBuyValue: 0,
      cumulativeFee: 0,
      // 正价值
      positiveValue: 0,
      source: [],
      totalTodayPnl: 0,
      todayIncrementBuyValueTotal: 0,
      todayUnPnl: 0,
    });
    const stockTodayValue = returnZeroIfInvalidNumber(stock?.todayValue);
    const tokenTodayValue = returnZeroIfInvalidNumber(token?.todayValue);
    const stockTotalTodayPnl = returnZeroIfInvalidNumber(stock?.totalTodayPnl);
    const tokenTotalTodayPnl = returnZeroIfInvalidNumber(token?.totalTodayPnl);
    const stockTodayUnPnl = returnZeroIfInvalidNumber(stock?.todayUnPnl);
    const tokenTodayUnPnl = returnZeroIfInvalidNumber(token?.todayUnPnl);
    const stockTodayIncrementBuyValueTotal = returnZeroIfInvalidNumber(
      stock?.todayIncrementBuyValueTotal,
    );
    const tokenTodayIncrementBuyValueTotal = returnZeroIfInvalidNumber(
      token?.todayIncrementBuyValueTotal,
    );
    const stockCumulativeNetBuyValue = returnZeroIfInvalidNumber(
      stock?.cumulativeNetBuyValue,
    );
    const tokenCumulativeNetBuyValue = returnZeroIfInvalidNumber(
      token?.cumulativeNetBuyValue,
    );
    const stockCumulativeBuyValue = returnZeroIfInvalidNumber(
      stock?.cumulativeBuyValue,
    );
    const tokenCumulativeBuyValue = returnZeroIfInvalidNumber(
      token?.cumulativeBuyValue,
    );
    const stockCumulativeFee = returnZeroIfInvalidNumber(stock?.cumulativeFee);
    const tokenCumulativeFee = returnZeroIfInvalidNumber(token?.cumulativeFee);
    const stockPrevValue = returnZeroIfInvalidNumber(stock?.prevValue);
    const tokenPrevValue = returnZeroIfInvalidNumber(token?.prevValue);
    // 使用 bigDecimal 进行精确计算
    const value = Number(
      new bigDecimal(stockTodayValue)
        .add(new bigDecimal(tokenTodayValue))
        .getValue(),
    );
    const totalTodayPnl = Number(
      new bigDecimal(stockTotalTodayPnl)
        .add(new bigDecimal(tokenTotalTodayPnl))
        .getValue(),
    );
    const todayIncrementBuyValueTotal = Number(
      new bigDecimal(stockTodayIncrementBuyValueTotal)
        .add(new bigDecimal(tokenTodayIncrementBuyValueTotal))
        .getValue(),
    );
    const todayUnPnl = Number(
      new bigDecimal(stockTodayUnPnl)
        .add(new bigDecimal(tokenTodayUnPnl))
        .getValue(),
    );
    const prevValue = Number(
      new bigDecimal(stockPrevValue)
        .add(new bigDecimal(tokenPrevValue))
        .getValue(),
    );
    const change = Number(
      new bigDecimal(value).subtract(new bigDecimal(prevValue)).getValue(),
    );
    const changePercent =
      prevValue === 0
        ? undefined
        : Number(
            new bigDecimal(change)
              .divide(new bigDecimal(prevValue))
              .multiply(new bigDecimal(100))
              .getValue(),
          );
    const cumulativeNetBuyValue = Number(
      new bigDecimal(stockCumulativeNetBuyValue)
        .add(new bigDecimal(tokenCumulativeNetBuyValue))
        .getValue(),
    );
    const cumulativeBuyValue = Number(
      new bigDecimal(stockCumulativeBuyValue)
        .add(new bigDecimal(tokenCumulativeBuyValue))
        .getValue(),
    );
    const cumulativeFee = Number(
      new bigDecimal(stockCumulativeFee)
        .add(new bigDecimal(tokenCumulativeFee))
        .getValue(),
    );
    const totalCumulativePnl = Number(
      new bigDecimal(value)
        .subtract(new bigDecimal(cumulativeNetBuyValue))
        .subtract(new bigDecimal(cumulativeFee))
        .getValue(),
    );
    const totalCumulativePnlPercent =
      cumulativeBuyValue === 0
        ? undefined
        : Number(
            new bigDecimal(totalCumulativePnl)
              .divide(new bigDecimal(cumulativeBuyValue))
              .multiply(new bigDecimal(100))
              .getValue(),
          );
    const positiveValue = Number(
      new bigDecimal(stock.positiveValue)
        .add(new bigDecimal(token.positiveValue))
        .getValue(),
    );
    const totalTodayPnlPercent =
      todayIncrementBuyValueTotal === 0
        ? undefined
        : Number(
            new bigDecimal(totalTodayPnl)
              .divide(new bigDecimal(todayIncrementBuyValueTotal))
              .multiply(new bigDecimal(100))
              .getValue(),
          );
    const todayUnPnlPercent =
      prevValue === 0
        ? undefined
        : Number(
            new bigDecimal(todayUnPnl)
              .divide(new bigDecimal(prevValue))
              .multiply(new bigDecimal(100))
              .getValue(),
          );
    // log.debug("[usePortfolioPage] totalCumulativePnl", {
    //   totalCumulativePnl,
    //   value,
    //   cumulativeNetBuyValue,
    //   cumulativeFee,
    //   stock,
    //   stocks: assets?.stocks,
    //   tokens: assets?.tokens,
    //   totalTodayPnlPercent,
    //   totalTodayPnl,
    //   todayIncrementBuyValueTotal,
    // });
    return {
      value,
      prevValue,
      stockPrevValue,
      tokenPrevValue,
      totalTodayPnl,
      todayUnPnl,
      todayUnPnlPercent,
      change,
      changePercent,
      totalCumulativePnlPercent,
      totalCumulativePnl,
      positiveValue,
      stockPositiveValue: stock.positiveValue,
      tokenPositiveValue: token.positiveValue,
      totalTodayPnlPercent,
      tokenSource: token.source,
      stockSource: stock.source,
    };
  }, [data?.assets, data?.forex]);
  /* 合并tokens和stocks 收藏列表
   * 这里的allData是给表格用的，其中value pnl等数据都是用自身货币单位计算的，没有做汇率转换，所以对于计算占比来说需要用tokenSource和stockSource ✅
   */
  const allData = useMemo(() => {
    if (!stockSource?.length && !tokenSource?.length) {
      return [];
    }
    const allData = [...stockSource, ...tokenSource].sort(
      (a, b) =>
        returnZeroIfInvalidNumber(b.value_usd || b.value) -
        returnZeroIfInvalidNumber(a.value_usd || a.value),
    );
    return allData;
  }, [tokenSource, stockSource]);
  const topPerformer = useMemo(() => {
    if (allData) {
      // 获取pnl_percent 最大的一个 利用lodash
      return maxBy(
        allData.filter((item) => typeof item.today_pnl_percent !== undefined),
        "today_pnl_percent",
      );
    }
    return undefined;
  }, [allData]);
  // 计算top5资产占比 对
  const topAssets = useMemo(() => {
    const allData = [...(tokenSource || []), ...(stockSource || [])];
    if (!allData) {
      return null;
    }
    // 取 value 最大的5个数据
    const allDataSorted = [...allData]
      .filter(
        (item) =>
          returnZeroIfInvalidNumber(Number(item.value_usd || item.value)) > 0,
      )
      .sort(
        (a, b) =>
          returnZeroIfInvalidNumber(b.value_usd) -
          returnZeroIfInvalidNumber(a.value_usd),
      );
    // 累计
    // let totalPercentage = 0;
    const top5Data = allDataSorted.map((item) => {
      const itemValueUsd = returnZeroIfInvalidNumber(
        item.value_usd || item.value,
      );
      const positiveValueNum = returnZeroIfInvalidNumber(positiveValue);
      const percentage = returnZeroIfInvalidNumber(
        Number(
          new bigDecimal(itemValueUsd)
            .divide(new bigDecimal(positiveValueNum))
            .multiply(new bigDecimal(100))
            .getValue(),
        ),
      );
      // totalPercentage += percentage;
      return {
        key: item.asset?.id || item.account_id || "",
        label:
          item.asset?.short_name || item.asset?.symbol || item.account_id || "",
        value: percentage,
        totalValue: item.value_usd,
        logo: item.asset?.logo,
        symbol: item.asset?.symbol,
        company_name: (item.asset as Stock).company_name,
      };
    });
    return top5Data;
  }, [positiveValue, tokenSource, stockSource]);

  // 分类计算 stock 和 token 的占比 ✅
  const assetTypes = useMemo(() => {
    const stockPercentage = returnZeroIfInvalidNumber(
      (returnZeroIfInvalidNumber(stockPositiveValue) /
        returnZeroIfInvalidNumber(positiveValue)) *
        100,
    );
    const tokenPercentage = returnZeroIfInvalidNumber(
      (returnZeroIfInvalidNumber(tokenPositiveValue) /
        returnZeroIfInvalidNumber(positiveValue)) *
        100,
    );
    const result = [
      {
        key: "stock",
        label: "Stock",
        value: stockPercentage,
      },
      {
        key: "crypto",
        label: "Crypto",
        value: tokenPercentage,
      },
    ].filter((item) => !!returnZeroIfInvalidNumber(item.value));
    return result;
  }, [positiveValue, stockPositiveValue, tokenPositiveValue]);

  // PNL-only performance chart (no BTC / S&P 500 benchmark lines)
  const performance = useMemo(() => {
    const chartData = genPerformance({
      pnl: data.performance,
    });
    if (!chartData) {
      return undefined;
    }

    const lastIndex = chartData.XAxis.length;
    chartData.XAxis.push({
      x: lastIndex,
      label: dayjs().format("MMM DD, HH:mm"),
    });
    chartData.YPnl.push(returnZeroIfInvalidNumber(totalCumulativePnlPercent));
    chartData.dataValue.push({
      timestamp: dayjs().valueOf(),
      pnl: {
        value: totalCumulativePnl || 0,
        percentage: returnZeroIfInvalidNumber(totalCumulativePnlPercent),
      },
    });
    return chartData;
  }, [data.performance, totalCumulativePnlPercent, totalCumulativePnl]);

  return {
    isPending,
    data: allData,
    value,
    change,
    changePercent,
    topPerformer,
    topAssets,
    isSuccess,
    isError,
    isTotalError,
    refetchTotal,
    isFetching,
    isLoading,
    isRefetching,
    totalTodayPnlPercent,
    totalTodayPnl,
    isStale,
    totalCumulativePnlPercent,
    totalCumulativePnl,
    performance,
    accountId: id,
    assetTypes,
    todayUnPnlPercent,
    todayUnPnl,
    refetch,
  };
};
